import { CellContext, createColumnHelper } from '@tanstack/react-table';
import React from 'react';
import styled from 'styled-components';

import { Client, GlobalAccount, GUID, PersonOrgType } from '@ndvr/customer-api';
import { PortfolioProfile } from '@ndvr/portfolio-api';

import { formatCurrency, formatNumberHuman, getClientName, REFERENCE_PORTFOLIOS, useHouseholdRoutePaths } from 'utils';
import { useClient } from 'views/client/client-queries';
import { AccountContextMenu } from 'views/household/AccountGroupTab/AccountContextMenu';
import { useAdvisorLogin, useGlobalAccount } from 'views/household/household-queries';
import { useAccount } from 'views/portfolio/account-queries';
import { Button, CondensedTable, HorizontalLayout, IconExternalLink, Text, TextLink } from 'widgets';

const columnHelper = createColumnHelper<PortfolioProfile>();

interface ColumnConfig {
    showGlobalAccount?: boolean;
    showClient?: boolean;
}

export const PortfolioProfileTable = ({
    portfolioProfiles,
    hideHeader,
    columnConfig = {},
}: {
    portfolioProfiles: PortfolioProfile[];
    hideHeader?: boolean;
    columnConfig?: ColumnConfig;
}) => {
    const { PortfolioPageRoutePath } = useHouseholdRoutePaths();
    portfolioProfiles.sort((a, b) => {
        const aumA = Number(a.performance?.marketValue);
        const aumB = Number(b.performance?.marketValue);
        return aumB - aumA;
    });

    const columns = [
        columnHelper.accessor((data) => data.portfolio.custodyAccountId, {
            id: 'custodyAccountId',
            header: 'Account',
            size: 112,
        }),
        columnHelper.accessor((data) => data.portfolio.name.plainText, {
            id: 'name',
            header: 'Account Name',
            cell: ({ getValue, row }) => (
                <TextLink
                    to={PortfolioPageRoutePath(
                        row.original.portfolio.globalAccountGUID,
                        row.original.portfolio.accountGroupGUID,
                    )}
                >
                    {getValue()}
                </TextLink>
            ),
            size: 450,
        }),
        columnHelper.accessor((data) => data.performance?.marketValue, {
            id: 'currentValue',
            header: 'Current Value',
            cell: ({ getValue, row }) => {
                const increaseInValue = row.original.performance
                    ? Number(row.original.performance.returnsPercentNet)
                    : undefined;
                const increaseInValuePrefix = increaseInValue && increaseInValue >= 0 ? '+' : '';

                return (
                    <HorizontalLayout $apart>
                        <Text $themeColor={getValue() ? 'primary' : 'subtle'}>
                            {formatCurrency(getValue(), row.original.portfolio.workingCurrency)}
                        </Text>
                        {increaseInValue && (
                            <Text $themeColor={increaseInValuePrefix ? 'positive' : 'negative'}>
                                {increaseInValuePrefix}
                                {formatNumberHuman(increaseInValue)}%
                            </Text>
                        )}
                    </HorizontalLayout>
                );
            },
            size: 170,
        }),
        columnHelper.accessor((data) => data.portfolio.investmentSetup?.referencePortfolio, {
            id: 'strategy',
            header: 'Strategy',
            cell: ({ getValue }) => <Text>{REFERENCE_PORTFOLIOS[getValue()]}</Text>,
        }),
        ...(columnConfig.showGlobalAccount
            ? [
                  columnHelper.accessor((data) => data.portfolio.globalAccountGUID, {
                      id: 'globalAccountGUID',
                      header: 'Household',
                      cell: ({ getValue }) => <AccountDisplay globalAccountGUID={getValue()} />,
                      size: 200,
                  }),
              ]
            : []),
        ...(columnConfig.showClient
            ? [
                  columnHelper.accessor((data) => data.portfolio.clientGUID, {
                      id: 'clientGUID',
                      header: 'Account Owner',
                      cell: ({ getValue }) => {
                          const guid = getValue();
                          return guid ? <ClientDisplay clientGUID={guid} /> : null;
                      },
                      size: 200,
                  }),
              ]
            : []),
        columnHelper.display({
            id: 'actions',
            // flexRender treats this as a component; a stable reference preserves drawer state across table renders.
            cell: PortfolioActions,
            size: 100,
        }),
    ];

    return <CondensedTable columns={columns} data={portfolioProfiles} hideHeader={hideHeader} />;
};

const AccountDisplay = ({ globalAccountGUID }: { globalAccountGUID: GUID<GlobalAccount> }) => {
    const { data: account } = useGlobalAccount(globalAccountGUID);
    return <Text>{account?.globalAccountNamePlain}</Text>;
};
const ClientDisplay = ({ clientGUID }: { clientGUID: GUID<Client> }) => {
    const { data: client } = useClient(clientGUID);
    return (
        <Text>
            {client && getClientName(client)}
            {client?.clientPersonOrgProfile.personOrg.personOrgType === PersonOrgType.PERSON && ' Individual'}
        </Text>
    );
};

const PortfolioActions = ({ row }: CellContext<PortfolioProfile, unknown>) => {
    const portfolioProfile = row.original;
    const advisorLoginMutation = useAdvisorLogin();

    const { data: account } = useAccount(portfolioProfile.portfolio.portfolioGUID, {
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });

    return (
        <HorizontalLayout $rowCenter $gap={16}>
            <Button
                size="icon"
                buttonType="ghost"
                mutation={advisorLoginMutation}
                mutationVar={{
                    redirect: `/portfolio/${portfolioProfile.portfolio.portfolioGUID}`,
                    householdGUID: portfolioProfile.portfolio.globalAccountGUID,
                }}
                icon={<StyledIconExternalLink size="xSmall" />}
            />
            {account && (
                <AccountContextMenu householdGUID={portfolioProfile.portfolio.globalAccountGUID} account={account} />
            )}
        </HorizontalLayout>
    );
};

const StyledIconExternalLink = styled(IconExternalLink)`
    margin-left: 4px;
`;
